import { isMapEditing } from './editlock';
import * as maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
// v6 ESM worker under a bundler: the dist worker imports its sibling
// maplibre-gl-shared.mjs, and ?url emits the worker file verbatim in
// production builds WITHOUT that sibling — the worker then fails on its first
// import and no vector tiles load. ?worker&url routes the file through Vite's
// worker pipeline instead, emitting a self-contained chunk. One call covers
// both Map instances (cockpit + Studio); it must run before either constructs.
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import './style.css';
import './panels.css';

maplibregl.setWorkerUrl(workerUrl);

import { openAdmin } from './admin';
import type { EntityDto, Me, RegistryDto, UpdatesDto } from './api';
import { ApiError, get, post, put, setUnauthorizedHandler } from './api';
import {
  BASEMAP_LABELS, OFM_STYLE, TERRAIN_EXAGGERATION, TERRAIN_SOURCE_ID,
  installBasemaps, installTerrain, setBasemap,
} from './basemaps';
import { buildLabel, watchForNewBuild } from './build';
import { installDiagnostics, notePoll } from './diag';
import type { LiveStateDto, TickerItem, ToolId } from './hud';
import {
  installGauges, installSearch, installTicker, isCompactViewport, toast,
} from './hud';
import {
  applyMapTheme, bumpTiles, ensureTypeImages, forceTileRefresh, needsTileRefresh,
  installObjectInteractions, installObjectLayers, LIVE_CLICK_IDS, loadRegistry,
} from './objects';
import { setEventMarkerConfig } from './eventstyle';
import { setTerritoryColorConfig } from './territory';
import { setLiveTiming } from './when';
import { setRegionFlash } from './boundaries';
import { installBuildings } from './buildings';
import { installPipelines } from './pipelines';
import { installUserBookmarkLayers } from './bookmarks';
import { installDayNight } from './daynight';
import { showDrawBar } from './drawbar';
import { installMeasure } from './measure';
import {
  closeMarkup, installMarkupLayers, isMarkupActive, markupEscape, openMarkup,
} from './markup';
import { cockpitSheetHost, geometryBbox } from './cockpitsheet';
import {
  activeSheetTitle, exportActiveSheet, isSheetPodOpen, openSheetPod, registerSheetHost,
  restoreActiveSheet, sheetEscape, toggleSheetPod, useSheet, type SheetDto,
} from './sheets';
import { installDomeLayer, setVolumeStyle, type VolumeStyle } from './domes';
import { installPanels, type PanelsApi } from './panels';
import { installRotateHandle } from './rotate';
import {
  closeSelection, mapPickActive, openSelection, selectedEntityId, tryConsumeMapPick,
} from './select';
import { copyViewLink, installShare, type ShareHooks } from './share';
import { cancelScanDraw, startScanDraw } from './scan';
import { openStudio } from './studio';
import {
  getActiveStory, getStories, getStory, loadStories, orderedStories, setStory,
} from './story';
import { cancelPlacement, iconSvg, openDeployPalette } from './tactical';
import {
  consumeTerritoryPick, startAreaFromCard, startClaimFromCard, startSplitFromCard,
} from './territory';
import {
  cancelTerritoryEdit, editTerritoryBoundary, installTerritoryEditor, isTerritoryEditActive,
} from './territoryedit';
import { closeTimeline, isTimeTravelling, openTimeline } from './timeline';

// Last stylesheet wins: the phone/touch layer overrides the desktop rules in
// style.css, panels.css and admin.css without those files having to know.
import './mobile.css';

const root = document.querySelector<HTMLDivElement>('#app')!;

// ---------------------------------------------------------------------------
// Theme (applies to login too, so it runs at module load)
// ---------------------------------------------------------------------------

const THEME_KEY = 'cockpit-theme';

function currentTheme(): 'light' | 'dark' {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

function applyTheme(theme: 'light' | 'dark'): void {
  document.documentElement.dataset.theme = theme;
  localStorage.setItem(THEME_KEY, theme);
  const btn = document.getElementById('theme-btn');
  if (btn) {
    btn.textContent = theme === 'dark' ? '☀' : '☾';
    btn.title = theme === 'dark' ? 'Light mode' : 'Dark mode';
  }
}

applyTheme((localStorage.getItem(THEME_KEY) as 'light' | 'dark' | null)
  ?? (matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'));

// ---------------------------------------------------------------------------
// Login (unchanged flow)
// ---------------------------------------------------------------------------

function renderLogin(message = ''): void {
  root.innerHTML = `
    <div class="login-wrap"><form class="login-card" id="loginForm">
      <h1>geoSentry</h1>
      <p>Satellite change &amp; event tracking</p>
      <p class="login-error" id="loginError">${message}</p>
      <div id="codeFields" style="display:none">
        <input id="code" type="password" placeholder="Access code" autocomplete="current-password" />
      </div>
      <div id="userFields">
        <input id="username" type="text" placeholder="Username" autocomplete="username" autofocus />
        <input id="password" type="password" placeholder="Password" autocomplete="current-password" />
      </div>
      <button type="submit">Enter</button>
      <div class="alt"><a id="altMode">Use an access code</a></div>
    </form></div>`;
  // ACCOUNT FIRST. The access-code path was the default, and it is a GUEST
  // session: it attaches to no app_user row, so nothing records who signed
  // in, "last login" stays blank for everyone, and per-user anything is
  // impossible. People typing the shared code believed they were logging in.
  // The code path stays one click away for genuine guest access.
  let mode: 'code' | 'user' = 'user';
  const codeFields = document.getElementById('codeFields')!;
  const userFields = document.getElementById('userFields')!;
  const alt = document.getElementById('altMode')!;
  alt.addEventListener('click', () => {
    mode = mode === 'code' ? 'user' : 'code';
    codeFields.style.display = mode === 'code' ? '' : 'none';
    userFields.style.display = mode === 'user' ? '' : 'none';
    alt.textContent = mode === 'code' ? 'I have an account' : 'Use an access code';
  });
  document.getElementById('loginForm')!.addEventListener('submit', async (e) => {
    e.preventDefault();
    const error = document.getElementById('loginError')!;
    const body = mode === 'code'
      ? { code: (document.getElementById('code') as HTMLInputElement).value }
      : { username: (document.getElementById('username') as HTMLInputElement).value,
          password: (document.getElementById('password') as HTMLInputElement).value };
    try {
      await post('/api/login', body);
      void boot();
    } catch (err) {
      error.textContent = err instanceof Error ? err.message : 'Login failed.';
    }
  });
}

// ---------------------------------------------------------------------------
// Cockpit
// ---------------------------------------------------------------------------

interface PublicConfig {
  defaultBasemap: string;
  /** The story a browser that has never chosen opens on; '' = All stories. */
  defaultStory?: string;
  defaultLayers: Record<string, boolean>;
  terrainExaggeration?: number;
  eventFadeHours?: number;
  eventColorHours?: number;
  eventFreshMinutes?: number;
  regionFlash?: unknown;
  eventMarkerStyles?: unknown;
  territoryColors?: unknown;
  volumeStyles?: Partial<Record<'radar' | 'engage', Partial<VolumeStyle>>>;
}

async function renderApp(me: Me): Promise<void> {
  root.innerHTML = `
    <div class="cockpit">
      <div id="map"></div>
      <header class="topbar">
        <span class="brand"><i>${iconSvg('range')}</i><span>geoSentry</span></span>
        <select id="story" title="Story — scope the map to one situation"></select>
        <span class="searchwrap">
          <i>${iconSvg('select')}</i>
          <input id="q" type="text" placeholder="Search places…" />
          <div class="search-results" id="q-results"></div>
        </span>
        <div class="ticker" id="ticker" title="Latest live event — click to fly there"></div>
        <div class="gauges" id="gauges"></div>
        <span class="account-wrap" id="account-wrap">
          <button class="iconbtn" id="account-btn" title="Account"></button>
        </span>
        <button class="iconbtn" id="share-btn" title="Copy a link to this view (right-click the map for a spot or an object)">⇪</button>
        <button class="iconbtn" id="daynight-btn" title="Day/night shadows: off">◐</button>
        <button class="iconbtn" id="theme-btn"></button>
      </header>
      <nav class="dock">
        <div class="dock-sec dock-panels">
          <!-- Only the live feed carries a badge: unread is a feed concept, and
               a dock full of numbers reads as seven notification bubbles.
               data-label is the phone tab bar's caption (mobile.css renders
               it under the icon); the desktop dock shows icons only. -->
          <button data-panel="live" data-label="Live" title="Live feed">${iconSvg('live')}<b class="bdg" hidden></b></button>
          <button data-panel="places" data-label="Places" title="Places">${iconSvg('area')}</button>
          <button data-panel="objects" data-label="Objects" title="Objects">${iconSvg('assets')}</button>
          <button data-panel="damage" data-label="Damage" title="Damage">${iconSvg('burst')}</button>
          <button data-panel="intel" data-label="Intel" title="Suggestions">${iconSvg('intel')}</button>
          <button data-panel="bookmarks" data-label="Marks" title="Bookmarks">${iconSvg('bookmark')}</button>
          <button data-panel="log" data-label="Log" title="Activity log">${iconSvg('log')}</button>
        </div>
        <div class="dock-div"></div>
        <div class="dock-sec dock-tools">
          <button data-tool="select" data-label="Select" title="Select">${iconSvg('select')}</button>
          <button data-tool="add" data-label="Add" title="Add to map (unit, place, actor)">${iconSvg('deploy')}</button>
          <button data-tool="scan" data-label="Scan" title="Scan · compare imagery">${iconSvg('scan')}</button>
          <button data-tool="measure" data-label="Measure" title="Measure distance">${iconSvg('ruler')}</button>
          <button data-tool="area" data-label="Area" title="Measure an area — ground and perimeter">${iconSvg('plot')}</button>
          <button data-tool="markup" data-label="Markup" title="Markup — arrows, notes and shapes only you see">${iconSvg('markup')}</button>
          <button data-tool="time" data-label="Replay" title="Time travel">${iconSvg('time')}</button>
        </div>
        <!-- Phone only (mobile.css): thirteen targets don't fit a phone's
             width, so the seven tools fold behind one tab and open as a
             sheet. -->
        <button class="dock-more" data-label="Tools" title="Tools">${iconSvg('layers')}</button>
      </nav>
      <aside class="drawer" id="drawer"></aside>
      <div class="map-rail" id="map-rail">
        <select id="basemap" title="Basemap"></select>
      </div>
      <div class="toasts" id="toasts"></div>
    </div>`;

  // loadStories() also resets its subscriber list, so it has to run before
  // anything subscribes — renderApp is re-entered on every login. It waits on
  // the config for the operator's default story; both still run alongside the
  // registry.
  const configLoad = get<PublicConfig>('/api/config');
  const [config, registry] = await Promise.all([
    configLoad,
    loadRegistry(),
    configLoad.then((c) => loadStories(c.defaultStory), () => loadStories()),
  ]);

  // Live-event timing: admin defaults from config, with each user's saved fade
  // override (localStorage) winning for their own browser. Set before any live
  // events render so the first paint already honours it.
  const storedFade = localStorage.getItem('cockpit-fade-hours');
  setLiveTiming({
    fadeHours: storedFade != null ? Number(storedFade) : (config.eventFadeHours ?? 12),
    colorHours: config.eventColorHours ?? 3,
    freshMinutes: config.eventFreshMinutes ?? 10,
  });
  // The region flash — kinds, pulses, timing, level (admin's Region flash tab).
  setRegionFlash(config.regionFlash);
  // How a new event announces itself (ring, pulse, symbol, colour stages).
  // Before the layers are installed, because installObjectLayers pre-builds the
  // glyph sprites the styles name — and before the first bake, for the same
  // reason the timing block above runs here.
  setEventMarkerConfig(config.eventMarkerStyles);
  // Who holds what ground, in what colour, and how contested ground is
  // hatched (062) — before the first entity poll derives the holder map.
  setTerritoryColorConfig(config.territoryColors);
  // Coverage-volume look: admin default, then the user's per-browser override.
  if (config.volumeStyles) setVolumeStyle(config.volumeStyles);
  try {
    const saved = localStorage.getItem('cockpit-volume-styles');
    if (saved) setVolumeStyle(JSON.parse(saved));
  } catch { /* ignore a corrupt override */ }

  const map = new maplibregl.Map({
    container: 'map', style: OFM_STYLE,
    center: [46.2, 28.8], zoom: 5, maxZoom: 20,
    // Shallower view angle — the default cap is 60°; 85° lets the camera drop
    // near the horizon (reads best on the globe / with terrain on).
    maxPitch: 85,
    // Tile speed vs. quality: cap the render resolution at 1.5× so high-DPI
    // screens stop fetching 2×/3× raster tiles (the main "waiting for tiles"
    // cost) — slightly softer, markedly fewer bytes. A bigger cache keeps
    // already-seen tiles instant when panning back.
    pixelRatio: Math.min(window.devicePixelRatio || 1, 1.5),
    maxTileCacheSize: 512,
    attributionControl: { compact: true },
    // v6 defaults this to 4 (slice overzoomed vector tiles instead of
    // overscaling), which changes queryRenderedFeatures results — and three
    // things here are DEFINED by qRF: the feature click, the rotate-grip hit
    // test, and the FIRMS viewport count. Pin the 5.x behaviour; opting into
    // slicing is its own change with its own visual pass.
    zoomLevelsToOverscale: undefined,
  });
  if (import.meta.env.DEV) (window as unknown as { __map: unknown }).__map = map;
  map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'bottom-right');
  map.addControl(new maplibregl.GlobeControl(), 'bottom-right');
  // Terrain toggle — sits with the globe/nav controls. The DEM source it drives
  // is installed on load (below); the button turns exaggerated relief on/off.
  // The vertical scale is an admin setting (defaults to TERRAIN_EXAGGERATION).
  map.addControl(new maplibregl.TerrainControl({
    source: TERRAIN_SOURCE_ID,
    exaggeration: config.terrainExaggeration ?? TERRAIN_EXAGGERATION,
  }), 'bottom-right');

  // Optional day/night mode: sun-driven skybox + shaded relief + a subtle night
  // tint. Off by default; the top-strip ◐ button toggles it, and the choice is
  // remembered across sessions (restored once the style + overlays are ready).
  const DAYNIGHT_KEY = 'cockpit-daynight';
  const daynight = installDayNight(map);
  const daynightBtn = document.getElementById('daynight-btn')!;
  const syncDaynightBtn = (on: boolean) => {
    daynightBtn.classList.toggle('active', on);
    daynightBtn.title = `Day/night shadows: ${on ? 'on' : 'off'}`;
  };
  daynightBtn.addEventListener('click', () => {
    const on = daynight.toggle();
    localStorage.setItem(DAYNIGHT_KEY, on ? '1' : '0');
    syncDaynightBtn(on);
  });

  // --- drawer + dock panels ------------------------------------------------
  const drawer = document.getElementById('drawer')!;
  const dockButton = (id: string) =>
    document.querySelector(`.dock [data-panel="${id}"]`) as HTMLElement;
  let panels: PanelsApi;

  // Studio runs its own MapLibre instance against its own tiles, so a damage
  // marker recorded in there is invisible to the cockpit until it is told.
  // Same pair of calls as the selection card's onDeleted, for the same reason.
  const studioHooks = {
    onEntityAdded: () => {
      forceTileRefresh(map);
      void panels.refresh();
    },
    // A sheet picked inside the Studio: a map sheet opens here on the cockpit,
    // another Studio sheet in a Studio of its own over its imagery area.
    onOpenSheet: (sheet: SheetDto) => {
      if (sheet.surface === 'studio') {
        openStudioSheet(sheet);
      } else {
        void useSheet(map, sheet, { fly: true, applyLayers: true })
          .then(() => openSheetPod(map));
      }
    },
  };

  /** Open a Studio sheet: the Studio over the imagery area it was saved with,
   *  its markup and pair restored. On the next tick, so a Studio that is
   *  closing to make way has let go first. */
  function openStudioSheet(sheet: SheetDto): void {
    const area = sheet.imagery?.area as GeoJSON.Polygon | undefined;
    const bb = (area ? geometryBbox(area) : null) ?? sheet.frame?.bbox ?? null;
    if (!bb) return;
    setTimeout(() => {
      void openStudio(bb, area?.type === 'Polygon' ? area : undefined, undefined, studioHooks,
                      { sheet });
    }, 0);
  }

  // Opening an entity means "show me this on the map". On a phone the drawer
  // is a bottom sheet in the same slot as the selection card, so mobile.css
  // slides the sheet away while a card is showing and brings it back when
  // the card closes — the list you came from is where ✕ returns you. Only a
  // FULL-height sheet is stood down here: it hides the map the flyTo lands
  // on, and a card closing into a full-screen list is not "back".
  const showEntity = (id: string): void => {
    if (isMapEditing()) return;
    if (isCompactViewport()) panels.peek();
    void openSelection(map, id, selectionHooks);
  };

  const selectionHooks = {
    onScanHere: (entity: EntityDto) => {
      const g = entity.geometry;
      if (!g) return;
      // Studio's boundary-redraw button only makes sense for entities whose
      // type can hold a polygon (a point base gaining an outline included).
      const canOutline = registry.types.find((t) => t.key === entity.type)
        ?.geometryKinds.includes('polygon');
      const placeRef = canOutline
        ? { id: entity.id, name: entity.name } : undefined;
      if (g.type === 'Point') {
        const [lon, lat] = g.coordinates as [number, number];
        const d = 0.02; // ~2 km half-box
        void openStudio([lon - d, lat - d, lon + d, lat + d], undefined, placeRef,
                        studioHooks);
      } else {
        const nums = JSON.stringify(g).match(/-?\d+\.?\d*/g)!.map(Number);
        const lons = nums.filter((_, i) => i % 2 === 0);
        const lats = nums.filter((_, i) => i % 2 === 1);
        const bbox: [number, number, number, number] = [
          Math.min(...lons), Math.min(...lats),
          Math.max(...lons), Math.max(...lats)];
        // Pass the polygon itself when we have one: the scene cache is keyed
        // by the request geometry, so this reuses scenes the Places pane
        // already fetched for the same shape.
        void openStudio(bbox, g.type === 'Polygon' ? g : undefined, placeRef,
                        studioHooks);
      }
    },
    onDeleted: () => {
      forceTileRefresh(map);
      void panels.refresh();
    },
    onOpenPeer: (id: string) => { void openSelection(map, id, selectionHooks); },
    onEdited: () => {
      forceTileRefresh(map);   // name/status ride the tiles; force a repaint
      void panels.refresh();
    },
    onEditGeometry: (entity: EntityDto, kind: 'point' | 'polygon') => { editGeometry(entity, kind); },
    // A territory's line is dragged in place (territoryedit.ts); the server
    // resolves what the move means for the neighbours. The card reopens on
    // the fresh version either way.
    onDragBoundary: (entity: EntityDto) => {
      closeSelection(map);
      editTerritoryBoundary(map, entity, {
        onSaved: () => { void panels.refresh(); },
        onDone: () => { void openSelection(map, entity.id, selectionHooks); },
      });
    },
    // The cut lives with the Layers legend (territory.ts), which owns the
    // piece-picking form; the card only starts it.
    onCutBoundary: (entity: EntityDto) => {
      closeSelection(map);
      startSplitFromCard(entity);
    },
    // Mark area: the gesture is territoryarea.ts, the plan-and-apply form is
    // the Layers legend's (territory.ts), same as the cut.
    onMarkArea: (entity: EntityDto) => {
      closeSelection(map);
      startAreaFromCard(entity);
    },
    onAddGround: (entity: EntityDto) => {
      closeSelection(map);
      startClaimFromCard(entity);
    },
  };

  // Geometry editing: draw a polygon (click corners, double-click) or place a
  // point (single click). `kind` is what the operator asked to lay down — it
  // may differ from the current geometry (a point base gaining an area outline),
  // and the tools replace the geometry wholesale, so the conversion is implicit.
  // The card is closed while the map is the input; on success the geometry PUT
  // opens a new version and the card reopens fresh.
  function editGeometry(entity: EntityDto, kind: 'point' | 'polygon'): void {
    closeSelection(map);
    if (kind === 'point') movePoint(entity);
    else void redrawBoundary(entity);
  }

  async function saveGeometry(entity: EntityDto, geometry: object,
                              done: string): Promise<void> {
    try {
      const updated = await put<EntityDto>(`/api/entities/${entity.id}`, { geometry });
      bumpTiles(map, updated.rev);
      void panels.refresh();
      toast(done);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not update the geometry.');
    }
    void openSelection(map, entity.id, selectionHooks);
  }

  // Reshape an existing polygon. Reuses the Add-area capture so editing shows
  // the SAME live preview (outline + vertices + rubber-band) as drawing — the
  // old collect-points path drew nothing, so an edit looked like it did nothing.
  async function redrawBoundary(entity: EntityDto): Promise<void> {
    const ring = await captureArea('the new boundary');
    if (!ring) { void openSelection(map, entity.id, selectionHooks); return; }
    await saveGeometry(entity, { type: 'Polygon', coordinates: [ring] },
                       'Boundary updated.');
  }

  function movePoint(entity: EntityDto): void {
    toast('Click the new location (Esc cancels).');
    // No edit lock: this waits for ONE map click and has nothing half-done
    // to protect. On a phone the panes are sheets over the map, so reaching
    // the map means tapping a sheet away first — a lock that refused or
    // cancelled on that tap made the gesture impossible there.
    map.getCanvas().style.cursor = 'crosshair';
    const cleanup = () => {
      map.off('click', onClick);
      document.removeEventListener('keydown', onKey);
      map.getCanvas().style.cursor = '';
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      cleanup();
      void openSelection(map, entity.id, selectionHooks);
    };
    const onClick = async (e: maplibregl.MapMouseEvent) => {
      cleanup();
      await saveGeometry(entity,
        { type: 'Point', coordinates: [e.lngLat.lng, e.lngLat.lat] },
        'Location updated.');
    };
    map.on('click', onClick);
    document.addEventListener('keydown', onKey);
  }

  let lastTicker: TickerItem | null = null;
  const ticker = installTicker(document.getElementById('ticker')!, () => {
    if (!lastTicker) return;
    if (lastTicker.coords) map.flyTo({ center: lastTicker.coords, zoom: 11 });
    if (lastTicker.id) void openSelection(map, lastTicker.id, selectionHooks);
  });

  panels = installPanels(drawer, {
    map, registry, me,
    openEntity: (id) => showEntity(id),
    dockButtons: {
      objects: dockButton('objects'), places: dockButton('places'),
      bookmarks: dockButton('bookmarks'), live: dockButton('live'),
      damage: dockButton('damage'),
      intel: dockButton('intel'), log: dockButton('log'),
    },
    layersHost: document.getElementById('map-rail') as HTMLElement,
    overlayDefaults: config.defaultLayers,
    onCounts: (c) => gauges.update(lastLive, c.alerts, c.objects),
    onLive: (info) => {
      lastTicker = info.latest;
      ticker.set(info.latest);
      gauges.setSpark(info.buckets);
    },
    // Placing a suggested site as an area reuses the Add-area draw tool; the
    // damage panel uses both capture hooks for its point/area add buttons.
    captureArea: (label) => captureArea(label),
    capturePoint: (label) => capturePoint(label),
  });

  // --- gauges --------------------------------------------------------------
  let lastLive: LiveStateDto | null = null;
  const gauges = installGauges(document.getElementById('gauges')!,
    // Superusers get the console too — it renders only their sections.
    Boolean(me.isOperator ?? me.isAdmin),
    // The console gets the same area-draw hook the panels use, so a story's
    // AOI can be drawn on the map instead of typed as numbers.
    () => openAdmin(registry, { captureArea: (label) => captureArea(label),
                                isAdmin: me.isAdmin }));
  async function pollLive(): Promise<void> {
    try {
      lastLive = await get<LiveStateDto>('/api/live/state');
    } catch {
      lastLive = null; // 503 = live not configured
    }
    const c = panels.counts();
    gauges.update(lastLive, c.alerts, c.objects);
  }
  void pollLive();
  setInterval(() => void pollLive(), 20000);

  // --- story scope ---------------------------------------------------------
  // Which situation the cockpit is looking at. What the choice DOES lives with
  // the subscribers (panels, objects) — this only records it. The control is
  // hidden when the server listed no stories at all (including when
  // /api/stories is unreachable), rather than offering a lone "All stories".
  {
    const storySelect = document.getElementById('story') as HTMLSelectElement;
    const all = document.createElement('option');
    all.value = '';
    all.textContent = 'All stories';
    storySelect.appendChild(all);
    for (const { story, depth } of orderedStories()) {
      const opt = document.createElement('option');
      opt.value = story.id;
      // No-break spaces: a <select> collapses ordinary leading whitespace, so
      // the tree depth would not read at all.
      opt.textContent = '\u00a0\u00a0'.repeat(depth) + story.name;
      storySelect.appendChild(opt);
    }
    storySelect.value = getActiveStory()?.id ?? '';
    storySelect.hidden = getStories().length === 0;
    storySelect.addEventListener('change',
      () => setStory(storySelect.value || null));
  }

  // --- sharing -------------------------------------------------------------
  // A link that opens someone else's map where this one is (share.ts). The
  // right-click menu and the hash reader install with the object layers.
  const shareHooks: ShareHooks = {
    openEntity: (id) => showEntity(id),
    selectedEntityId,
    currentStory: () => getActiveStory()?.id ?? null,
    applyStory: (id) => {
      if (id && !getStory(id)) return false;
      setStory(id);
      (document.getElementById('story') as HTMLSelectElement).value = id ?? '';
      return true;
    },
    blocked: () => isMapEditing() || isTerritoryEditActive(),
  };
  document.getElementById('share-btn')!
    .addEventListener('click', () => copyViewLink(map, shareHooks));

  // --- basemap + search ----------------------------------------------------
  const basemapSelect = document.getElementById('basemap') as HTMLSelectElement;
  for (const [key, label] of Object.entries(BASEMAP_LABELS)) {
    const opt = document.createElement('option');
    opt.value = key;
    opt.textContent = label;
    basemapSelect.appendChild(opt);
  }
  basemapSelect.value = config.defaultBasemap in BASEMAP_LABELS ? config.defaultBasemap : 'ofm';
  let userPickedBasemap = false;
  basemapSelect.addEventListener('change', () => {
    userPickedBasemap = true;
    setBasemap(map, basemapSelect.value);
  });
  installSearch(document.getElementById('q') as HTMLInputElement,
                document.getElementById('q-results')!, map);

  // Theme toggle. Going dark pairs the map with the dark basemap (and back) —
  // but only until the user picks a basemap themselves; their choice wins.
  const configBasemap = basemapSelect.value;
  applyTheme(currentTheme());   // sync the button glyph now that it exists
  if (currentTheme() === 'dark' && !userPickedBasemap && 'dark' in BASEMAP_LABELS) {
    basemapSelect.value = 'dark';
  }
  // --- account: who am I, and how do I leave ------------------------------
  {
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    const wrap = document.getElementById('account-wrap')!;
    const btn = document.getElementById('account-btn')!;
    // The name itself, not initials — "EN" for an access code labelled "env
    // access code" told you nothing about who you were signed in as.
    btn.innerHTML = `${iconSvg('user')}<span>${esc(me.displayName)}</span>`;
    btn.title = `Signed in as ${me.displayName}`;
    const pop = document.createElement('div');
    pop.className = 'account-pop';
    pop.hidden = true;
    const role = me.kind === 'guest' ? 'Guest access'
      : me.isAdmin ? 'Administrator'
      : me.isSuperuser ? 'Superuser' : 'User';
    pop.innerHTML = `
      <div class="acct-who"><small>Signed in as</small>
        <b>${esc(me.displayName)}</b><span>${role}</span></div>
      ${me.kind === 'guest'
        ? '<p class="acct-note">Sign in with an account for admin tools.</p>' : ''}
      <button class="acct-item" id="acct-signout">Sign out</button>`;
    wrap.appendChild(pop);
    btn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      pop.hidden = !pop.hidden;
    });
    document.addEventListener('click', (ev) => {
      if (!pop.hidden && !pop.contains(ev.target as Node) && ev.target !== btn) {
        pop.hidden = true;
      }
    });
    pop.querySelector('#acct-signout')!.addEventListener('click', async () => {
      try {
        await post('/api/logout');
      } catch { /* clearing the cookie locally is enough to land on login */ }
      // Reload rather than re-render: the poll timers this session started
      // would otherwise keep firing and 401 over the login form.
      location.reload();
    });
  }

  document.getElementById('theme-btn')!.addEventListener('click', () => {
    const next = currentTheme() === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    applyMapTheme(map, next === 'dark');
    if (!userPickedBasemap && 'dark' in BASEMAP_LABELS) {
      basemapSelect.value = next === 'dark' ? 'dark' : configBasemap;
      setBasemap(map, basemapSelect.value);
    }
  });

  // --- dock tools ----------------------------------------------------------
  const measure = installMeasure(map);
  let activeTool: ToolId = 'select';
  const toolButtons = document.querySelectorAll<HTMLButtonElement>('.dock-tools [data-tool]');
  const setActiveTool = (tool: ToolId) => {
    activeTool = tool;
    toolButtons.forEach((b) => b.classList.toggle('active', b.dataset.tool === tool));
  };
  // Phone: the tools live behind one "Tools" tab and open as a sheet above
  // the bar (mobile.css). Picking a tool or tapping anywhere else closes it;
  // on the desktop dock the button is display:none and the class is inert.
  const dock = document.querySelector('.dock') as HTMLElement;
  const moreBtn = dock.querySelector('.dock-more') as HTMLButtonElement;
  moreBtn.addEventListener('click', () => dock.classList.toggle('tools-open'));
  document.addEventListener('pointerdown', (e) => {
    if (!dock.contains(e.target as Node)) dock.classList.remove('tools-open');
  });
  // The markup bar names the sheet shapes go to; its Sheet button opens the
  // flyout under it and its Export button exports as set up (sheets.ts).
  const markupOpts = {
    // Inside the cockpit, not on body: the "move right of an open drawer"
    // rule is .cockpit:has(.drawer.open) .markup-bar, and a fixed pod on
    // body would never match it.
    mount: document.querySelector('.cockpit') as HTMLElement,
    sheetLabel: () => activeSheetTitle(map),
    onSheet: () => toggleSheetPod(map),
    sheetOpen: () => isSheetPodOpen(map),
    onExport: () => { void exportActiveSheet(map); },
    onClose: () => setActiveTool('select'),
  };
  toolButtons.forEach((btn) => btn.addEventListener('click', () => {
    if (isMapEditing()) return;
    dock.classList.remove('tools-open');
    let tool = btn.dataset.tool as ToolId;
    if (tool === activeTool && tool !== 'select') tool = 'select';
    setActiveTool(tool);
    // Every tool but Select is worked ON THE MAP; on a phone the open sheet
    // covers the half of it the drawing would land in.
    if (tool !== 'select' && isCompactViewport()) panels.open(null);
    cancelScanDraw(map);
    cancelPlacement();
    stopAreaDraw();
    stopPointCapture();
    cancelTerritoryEdit();
    closeMarkup(map);
    measure.stop();
    if (tool !== 'time' && isTimeTravelling()) closeTimeline(map);
    if (tool === 'add') void pickTypeThenPlace();
    if (tool === 'scan') {
      startScanDraw(map, (bbox) => void openStudio(bbox, undefined, undefined, studioHooks),
                    () => setActiveTool('select'));
    }
    if (tool === 'measure') measure.start('distance');
    if (tool === 'area') measure.start('area');
    if (tool === 'markup') openMarkup(map, markupOpts);
    if (tool === 'time') openTimeline(map);
  }));
  setActiveTool('select');

  // Escape backs out of whatever mode is armed and returns to Select.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    // The sheet pod (or a frame being dragged) goes first, whatever the tool.
    if (sheetEscape(map)) return;
    if (activeTool === 'select') return;
    if (activeTool === 'markup' && markupEscape(map)) {
      if (!isMarkupActive()) setActiveTool('select');
      return;
    }
    cancelScanDraw(map);
    cancelPlacement();
    stopAreaDraw();
    measure.stop();
    if (isTimeTravelling()) closeTimeline(map);
    setActiveTool('select');
  });

  // Deploy asset: browse the registry, designate a position, then commit a
  // configured unit with optional base assignment.
  async function pickTypeThenPlace(): Promise<void> {
    await openDeployPalette(registry, map, {
      onCreated: (created) => {
        bumpTiles(map, created.rev);
        void panels.refresh();
        void openSelection(map, created.id, selectionHooks);
        setActiveTool('select');
      },
      onCancel: () => setActiveTool('select'),
      // The palette already pushed the new type into the shared registry
      // object; give its map markers an image.
      onTypeCreated: () => ensureTypeImages(map),
      // A polygon-capable place type: draw its footprint instead of a point.
      onDrawArea: (type) => drawAreaForType(type),
    });
  }

  // Draw area: click vertices, Finish on the draw bar, pick an area type.
  // A live preview (outline + vertices + a rubber-band edge to the cursor)
  // makes each click visible — without it the tool felt dead.
  let areaPoints: [number, number][] = [];
  let areaCleanup: (() => void) | null = null;

  function ensureAreaDrawLayers(): void {
    if (map.getSource('area-draw')) return;
    map.addSource('area-draw',
      { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({
      id: 'area-draw-fill', type: 'fill', source: 'area-draw',
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: { 'fill-color': '#ffb300', 'fill-opacity': 0.25 },
    });
    // A white casing under the line so the boundary reads on ANY basemap —
    // faint amber alone vanished over satellite imagery.
    map.addLayer({
      id: 'area-draw-casing', type: 'line', source: 'area-draw',
      filter: ['==', ['geometry-type'], 'LineString'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#ffffff', 'line-width': 5, 'line-opacity': 0.9 },
    });
    map.addLayer({
      id: 'area-draw-line', type: 'line', source: 'area-draw',
      filter: ['==', ['geometry-type'], 'LineString'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#ff8f00', 'line-width': 2.4, 'line-dasharray': [2, 1.3] },
    });
    map.addLayer({
      id: 'area-draw-verts', type: 'circle', source: 'area-draw',
      filter: ['==', ['geometry-type'], 'Point'],
      paint: {
        'circle-radius': 5.5, 'circle-color': '#ffffff',
        'circle-stroke-color': '#ff8f00', 'circle-stroke-width': 2.5,
      },
    });
  }

  function drawAreaPreview(cursor: [number, number] | null): void {
    const src = map.getSource('area-draw') as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    const feats: GeoJSON.Feature[] = [];
    const line = cursor ? [...areaPoints, cursor] : areaPoints;
    if (line.length >= 2) {
      feats.push({ type: 'Feature', properties: {},
        geometry: { type: 'LineString', coordinates: line } });
    }
    if (areaPoints.length >= 3) {
      feats.push({ type: 'Feature', properties: {},
        geometry: { type: 'Polygon', coordinates: [[...areaPoints, areaPoints[0]!]] } });
    }
    for (const p of areaPoints) {
      feats.push({ type: 'Feature', properties: {},
        geometry: { type: 'Point', coordinates: p } });
    }
    src.setData({ type: 'FeatureCollection', features: feats });
  }

  // Capture a polygon footprint on the map: click vertices, double-click to
  // finish, Esc (or any tool switch, via stopAreaDraw) cancels. Resolves the
  // closed ring, or null if cancelled. Shared by the Add-area flow and by
  // placing a suggested site as an area rather than a point.
  function captureArea(label: string): Promise<[number, number][] | null> {
    return new Promise((resolve) => {
      areaPoints = [];
      ensureAreaDrawLayers();
      drawAreaPreview(null);
      map.getCanvas().style.cursor = 'crosshair';
      map.doubleClickZoom.disable();
      let done = false;
      const finish = () => {
        if (areaPoints.length < 3) return;
        const ring: [number, number][] = [...areaPoints, areaPoints[0]!];
        done = true;
        stopAreaDraw();
        resolve(ring);
      };
      // Explicit Finish/Undo/Cancel — a double TAP cannot close a ring on a
      // phone, which left this flow with no way out (see drawbar.ts).
      const bar = showDrawBar({
      surface: map.getCanvas(),
        title: label.charAt(0).toUpperCase() + label.slice(1),
        minPoints: 3, pointShortcuts: true,
        hint: 'Click corners; click the first corner or press Enter to finish. Backspace undoes. Drag the map to pan.',
        minHint: 'Click at least 3 corners',
        onFinish: finish,
        onUndo: () => {
          areaPoints.pop();
          drawAreaPreview(null);
          bar.update(areaPoints.length);
        },
        onCancel: () => stopAreaDraw(),
      });
      const onClick = (e: maplibregl.MapMouseEvent) => {
        if (e.originalEvent.detail > 1) return;
        if (areaPoints.length >= 3) {
          const first = map.project(areaPoints[0]!);
          if (Math.hypot(e.point.x - first.x, e.point.y - first.y) <= 12) { finish(); return; }
        }
        areaPoints.push([e.lngLat.lng, e.lngLat.lat]);
        drawAreaPreview(null);
        bar.update(areaPoints.length);
      };
      const onMove = (e: maplibregl.MapMouseEvent) => {
        if (areaPoints.length) drawAreaPreview([e.lngLat.lng, e.lngLat.lat]);
      };
      const onDbl = (e: maplibregl.MapMouseEvent) => {
        e.preventDefault();
        if (areaPoints.length < 3) { toast('Need at least three corners.'); return; }
        finish();
      };
      map.on('click', onClick);
      map.on('mousemove', onMove);
      map.on('dblclick', onDbl);
      areaCleanup = () => {
        bar.close();
        map.off('click', onClick);
        map.off('mousemove', onMove);
        map.off('dblclick', onDbl);
        map.doubleClickZoom.enable();
        map.getCanvas().style.cursor = '';
        (map.getSource('area-draw') as maplibregl.GeoJSONSource | undefined)
          ?.setData({ type: 'FeatureCollection', features: [] });
        if (!done) resolve(null);
      };
    });
  }

  // Invoked from the Add palette once a polygon-capable place type is chosen —
  // the type is already known, so this only captures the shape.
  async function drawAreaForType(type: RegistryDto['types'][number]): Promise<void> {
    const ring = await captureArea(type.label);
    if (!ring) { setActiveTool('select'); return; }
    const name = prompt(`Name this ${type.label}:`) ?? '';
    const created = await post<EntityDto>('/api/entities', {
      type: type.key, name: name || null,
      geometry: { type: 'Polygon', coordinates: [ring] },
    });
    bumpTiles(map, created.rev);
    void panels.refresh();
    void openSelection(map, created.id, selectionHooks);
    setActiveTool('select');
  }
  function stopAreaDraw(): void {
    areaCleanup?.();
    areaCleanup = null;
  }

  // Capture a single point on the map: one click resolves it, Esc (or any tool
  // switch, via stopPointCapture) cancels with null. Used by the damage panel.
  let pointCleanup: (() => void) | null = null;
  function capturePoint(label: string): Promise<[number, number] | null> {
    return new Promise((resolve) => {
      stopPointCapture();
      toast(`Click the map to place ${label}. Esc cancels.`);
      map.getCanvas().style.cursor = 'crosshair';
      let done = false;
      // No edit lock — see movePoint: a one-click drop must stay armed while
      // the operator taps a sheet away to reach the map.
      const onClick = (e: maplibregl.MapMouseEvent) => {
        done = true;
        stopPointCapture();
        resolve([e.lngLat.lng, e.lngLat.lat]);
      };
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') stopPointCapture();
      };
      map.once('click', onClick);
      document.addEventListener('keydown', onKey);
      pointCleanup = () => {
        map.off('click', onClick);
        document.removeEventListener('keydown', onKey);
        map.getCanvas().style.cursor = '';
        pointCleanup = null;
        if (!done) resolve(null);
      };
    });
  }
  function stopPointCapture(): void {
    pointCleanup?.();
  }

  // --- map layers + updates ------------------------------------------------
  const whenStyleReady = (fn: () => void) => {
    if (map.isStyleLoaded() || (map.style as unknown as { _loaded?: boolean })?._loaded) fn();
    else map.once('style.load', fn);
  };
  whenStyleReady(() => {
    installBasemaps(map);
    installTerrain(map);
    // Before object layers, so entity dots and labels stack above the massing.
    installBuildings(map);
    // Pipelines too: reference lines under every object drawn on them.
    installPipelines(map);
    installObjectLayers(map);
    installUserBookmarkLayers(map);
    // Markup draws over everything else on the map: it is the operator's own
    // annotation of what the map shows, so it is never hidden behind it.
    installMarkupLayers(map);
    // Sheets: the composition markup belongs to, and the cockpit's export.
    registerSheetHost(cockpitSheetHost(map, {
      panels,
      basemapSelect,
      openStudioSheet,
      startMarkup: () => {
        if (activeTool !== 'markup') {
          (document.querySelector('[data-tool="markup"]') as HTMLButtonElement | null)?.click();
        }
      },
    }));
    void restoreActiveSheet(map);
    installDomeLayer(map, {   // 3D AD coverage domes (custom WebGL, toggled in panels)
      onSelect: (id) => void openSelection(map, id, selectionHooks),
    });
    installRotateHandle(map);   // grip layers exist up front (query guard below)
    installTerritoryEditor(map); // boundary-drag layers, above everything drawn so far
    applyMapTheme(map, currentTheme() === 'dark');
    setBasemap(map, basemapSelect.value);
    panels.applyOverlays();

    installShare(map, shareHooks);   // after the layers: a shared link may open a card
    installObjectInteractions(map, (id) => {
      if (isMapEditing()) return;
      // A card's armed "pick on the map" flow eats the click — linking, not
      // navigating (select.ts owns the mode).
      if (tryConsumeMapPick(id)) return;
      // The Layers legend's "Reassign district" flow reads the DISTRICT under
      // the click (territory.ts); the territory polygon under it is not the
      // thing being picked, so its card must not open.
      if (consumeTerritoryPick()) return;
      // A boundary drag's release lands on the territory being dragged; the
      // editor owns every click until Finish/Cancel.
      if (isTerritoryEditActive()) return;
      // Replay is a VIEW, not a drawing tool: a marker under the playhead is
      // still an object, and the card is how you read what it was. Every
      // other tool owns the click for its own gesture.
      if (activeTool !== 'select' && activeTool !== 'time') return;
      void openSelection(map, id, selectionHooks);
    });
    map.on('click', (e) => {
      if (isMapEditing()) return;
      const hits = map.queryRenderedFeatures(e.point,
        { layers: ['entities-symbol', 'entities-ring', 'entities-damage',
                   'entities-fill', 'entities-line', 'control-fill', ...LIVE_CLICK_IDS,
                   'dome-pick-fill', 'rotate-grip'] });   // grip/dome clicks don't close
      // A missed click during a pick/place flow must not close the card — the
      // card IS the flow.
      if (!hits.length && (activeTool === 'select' || activeTool === 'time')
          && !mapPickActive()) {
        closeSelection(map);
      }
    });

    // Overlay layers exist now, so the relief can slot in beneath them.
    if (localStorage.getItem(DAYNIGHT_KEY) === '1') {
      daynight.setActive(true);
      syncDaynightBtn(true);
    }

    void panels.refresh();
    startUpdatesLoop();
  });

  let since = 0;
  function startUpdatesLoop(): void {
    const poll = async () => {
      try {
        const dto = await get<UpdatesDto>(`/api/updates?since=${since}`);
        if (dto.rev > since) {
          // ONLY what the TILES draw justifies a tile re-fetch. This used to
          // be `some((e) => e.geometry)`, which is true of a live event — and
          // live events are both the commonest update and the one thing no
          // tile layer renders (needsTileRefresh explains why). Every such
          // poll re-fetched the whole viewport, aborted whatever was in
          // flight, and busted Martin's cache, for a layer drawn entirely
          // client-side.
          const tileWorthy = dto.entities.filter(needsTileRefresh).length;
          const geometryChanged = tileWorthy > 0 || dto.deleted.length > 0;
          if (geometryChanged && !isTimeTravelling()) bumpTiles(map, dto.rev);
          notePoll({ since: dto.rev, truncated: dto.truncated,
                     entityChanges: dto.entities.length, tileWorthy });
          // applyUpdates folds the delta into the panes itself — no refetch.
          if (dto.entities.length || dto.deleted.length || dto.notifications.length) {
            panels.applyUpdates(dto);
          }
          // Advance the cursor only after a SUCCESSFUL apply. It used to move
          // first, so one exception inside a render swallowed by the catch
          // below consumed the batch forever — the feed froze while the
          // cursor marched on, with nothing anywhere saying so. Now a
          // throwing apply retries the same batch next poll.
          since = dto.rev;
        }
      } catch (err) {
        // Transient by design — the cursor has not moved, so the next poll
        // retries the same batch. COUNTED rather than logged: a cockpit tab
        // runs for days, and one console line per failed poll is exactly how
        // a console becomes unreadable. sentinel.stats() has the tally.
        notePoll({ failed: true,
                   lastError: err instanceof Error ? err.message : String(err) });
      }
    };
    void poll();
    setInterval(() => void poll(), 15000);
  }

  (window as { __map?: unknown }).__map = map;
}

// ---------------------------------------------------------------------------

function webgl2Available(): boolean {
  try {
    const gl = document.createElement('canvas').getContext('webgl2');
    gl?.getExtension('WEBGL_lose_context')?.loseContext();
    return gl != null;
  } catch {
    return false;
  }
}

// Signed in, and the cockpit still failed to build. Said as that, with the
// error, because this used to be the LOGIN screen: boot() caught everything,
// so a browser that had turned WebGL off showed a blank sign-in form, the
// login succeeded (the server logged 200 after 200), renderApp threw again,
// and the form came back with no word of why.
function renderStartupError(err: unknown): void {
  const gpu = !webgl2Available();
  root.innerHTML = `
    <div class="login-wrap"><div class="login-card">
      <h1>geoSentry</h1>
      <p>You are signed in, but the map could not start.</p>
      <p class="boot-why" id="bootWhy"></p>
      <pre class="boot-detail" id="bootDetail"></pre>
      <button type="button" id="bootReload">Reload</button>
      <div class="alt"><a id="bootSignOut">Sign out</a></div>
    </div></div>`;
  document.getElementById('bootWhy')!.textContent = gpu
    ? 'This browser is not giving the site WebGL, which the map draws with. '
      + 'Chrome and Edge switch it off for a site after a graphics (GPU) crash '
      + 'and keep it off until the browser restarts: close every window of the '
      + 'browser and open it again. If it stays off, check that hardware '
      + 'acceleration is on (Settings → System) and look at chrome://gpu.'
    : 'Reloading usually clears it. If it does not, the error below is what to report.';
  document.getElementById('bootDetail')!.textContent =
    err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  document.getElementById('bootReload')!.addEventListener('click', () => location.reload());
  document.getElementById('bootSignOut')!.addEventListener('click', () => {
    void post('/api/logout').catch(() => undefined).then(() => renderLogin());
  });
}

async function boot(): Promise<void> {
  // Names the bundle the BROWSER actually has. Immutable asset caching means
  // a long-open tab can run old code against a freshly deployed server, which
  // reads exactly like a feature that failed to ship — so say it out loud.
  console.info(`sentinel build ${buildLabel()} — sentinel.stats() for the update cursor, poll health and tile churn`);
  installDiagnostics();
  watchForNewBuild();
  setUnauthorizedHandler(() => renderLogin('Session ended — sign in again.'));
  let me: Me;
  try {
    me = await get<Me>('/api/me');
  } catch {
    renderLogin();
    return;
  }
  try {
    await renderApp(me);
  } catch (err) {
    // A 401 mid-build already put the login screen up (the unauthorized
    // handler); anything else is the app failing, never "not signed in".
    if (err instanceof ApiError && err.status === 401) return;
    console.error('sentinel: the cockpit failed to start', err);
    renderStartupError(err);
  }
}

void boot();
